import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

import { onMounted, onUnmounted, ref, watch, nextTick } from 'vue'
import * as d3 from 'd3'

export interface Axis {
  label: string
  log?: boolean
  scientific?: boolean
}

export interface DataPoint {
  x: number
  y: number
}

export interface Curve {
  label: string
  data: DataPoint[]
}

export interface Legend {
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left'
}


export default /*@__PURE__*/_defineComponent({
  __name: 'D3Plot',
  props: {
    axes: {},
    curves: {},
    legend: {},
    palette: {},
    margin: {}
  },
  setup(__props: any) {

const props = __props

const plot = ref(null)

const primaryColor = getComputedStyle(document.documentElement)
  .getPropertyValue('--pico-color')
  .trim()

const render = async () => {
  if (!plot.value) return
  await nextTick()

  // Initialize plot parameters
  const margin = props.margin ?? 100
  const vMargin = margin / 2
  const width = plot.value.clientWidth
  const height = plot.value.clientHeight
  const palette = props.palette ?? d3.schemeCategory10

  // Clear previous contents
  d3.select(plot.value).selectAll('*').remove()

  // Create SVG element
  const svgElem = d3
    .select(plot.value)
    .append('svg')
    .attr('width', width)
    .attr('height', height)

  // Draw curves
  const allPoints = props.curves.flatMap((curve) => curve.data)
  const xMin = d3.min(allPoints, (d) => d.x)
  const yMin = d3.min(allPoints, (d) => d.y)
  const xMax = d3.max(allPoints, (d) => d.x)
  const yMax = d3.max(allPoints, (d) => d.y)

  const curves = svgElem.append('g').attr('class', 'curves')

  const xScale = props.axes.x.log ? d3.scaleLog() : d3.scaleLinear()
  xScale.domain([xMin, xMax]).range([margin, width - margin])

  const xAxis = props.axes.x.scientific
    ? d3.axisBottom(xScale).ticks(10, 'e')
    : d3.axisBottom(xScale)

  curves
    .append('g')
    .attr('transform', `translate(0, ${height - vMargin})`)
    .call(xAxis)
    .append('text')
    .attr('x', width / 2)
    .attr('y', margin / 2)
    .attr('fill', primaryColor)
    .attr('text-anchor', 'middle')
    .text(props.axes.x.label)

  const yScale = props.axes.y.log ? d3.scaleLog() : d3.scaleLinear()
  yScale.domain([yMin, yMax]).range([height - vMargin, vMargin])

  const yAxis = props.axes.y.scientific
    ? d3.axisLeft(yScale).ticks(10, 'e')
    : d3.axisLeft(yScale)

  curves
    .append('g')
    .attr('transform', `translate(${margin}, 0)`)
    .call(yAxis)
    .append('text')
    .attr('transform', 'rotate(-90)')
    .attr('x', -height / 2)
    .attr('y', -margin / 2)
    .attr('fill', primaryColor)
    .attr('text-anchor', 'middle')
    .text(props.axes.y.label)

  // Draw data
  const toLine = d3
    .line<DataPoint>()
    .x((d) => xScale(d.x))
    .y((d) => yScale(d.y))

  const lines = svgElem.append('g').attr('class', 'lines')

  lines
    .selectAll('path.line')
    .data(props.curves)
    .join('path')
    .attr('class', 'line')
    .attr('d', (curve) => toLine(curve.data))
    .attr('fill', 'none')
    .attr('stroke', (_, idx) => palette[idx % palette.length])
    .attr('stroke-width', 2)

  // Draw legends
  const legends = svgElem.append('g').attr('class', 'legend')
  legends
    .selectAll('rect.legend')
    .data(props.curves)
    .join('rect')
    .attr('class', 'legend')
    .attr('x', 10)
    .attr('y', (_, idx) => idx * 25)
    .attr('width', 20)
    .attr('height', 20)
    .attr('fill', (_, idx) => palette[idx % palette.length])
  legends
    .selectAll('text.legend-label')
    .data(props.curves)
    .join('text')
    .attr('class', 'legend-label')
    .attr('text-anchor', 'start')
    .attr('x', 35)
    .attr('y', (_, idx) => idx * 25 + 15)
    .text((axis) => axis.label)

  // Translate the legends based on position
  const legendsWidth = d3.max(
    legends.selectAll('text.legend-label').nodes(),
    (node) => node.getBBox().width
  )
  var legendXOffset = 0
  var legendYOffset = 0
  switch (props.legend?.position) {
    case 'top-right':
      legendXOffset = width - margin - legendsWidth - 50
      legendYOffset = vMargin
      break
    case 'bottom-right':
      legendXOffset = width - margin - legendsWidth - 50
      legendYOffset = height - vMargin - props.curves.length * 25 - 5
      break
    case 'top-left':
      legendXOffset = margin
      legendYOffset = vMargin
      break
    case 'bottom-left':
      legendXOffset = margin
      legendYOffset = height - vMargin - props.curves.length * 25 - 5
      break
  }
  legends.attr('transform', `translate(${legendXOffset}, ${legendYOffset})`)

  // Focus group to display coordinates on hover
  const focus = svgElem
    .append('g')
    .attr('class', 'focus')
    .style('display', 'none')

  focus.append('circle').attr('r', 4.5)
  focus.append('text').attr('x', 9).attr('dy', '.35em')

  // Find the closest data point in all curves
  //
  // NOTE: this works on the graph coordinates (not the data coordinates) to
  // simplify handling of logarithmic scales.
  function closestPointAllCurves(x, y) {
    let closestPoint = null
    let closestDistance = Infinity
    let allPoints = props.curves.flatMap((curve) => curve.data)

    for (const point of allPoints) {
      const dx = x - xScale(point.x)
      const dy = y - yScale(point.y)
      const distance = Math.sqrt(dx * dx + dy * dy)
      if (distance < closestDistance) {
        closestDistance = distance
        closestPoint = point
      }
    }
    return {
      x: xScale(closestPoint.x),
      y: yScale(closestPoint.y),
    }
  }

  // Formatters for coordinates
  const xFormat = (n) =>
    props.axes.x.scientific ? n.toExponential(3) : n.toFixed(3)
  const yFormat = (n) =>
    props.axes.y.scientific ? n.toExponential(3) : n.toFixed(3)

  // Mouse move handler
  function mousemove(event) {
    // Get mouse coordinates
    const xCoord = d3.pointer(event)[0]
    const yCoord = d3.pointer(event)[1]

    // Find closest data point in all curves
    const closestCoord = closestPointAllCurves(xCoord, yCoord)
    const closestX = xScale.invert(closestCoord.x)
    const closestY = yScale.invert(closestCoord.y)

    // Update focus position and text
    focus.attr('transform', `translate(${closestCoord.x},${closestCoord.y})`)
    focus.select('text').text(`(${xFormat(closestX)}, ${yFormat(closestY)})`)
  }

  // Overlay rectangle to capture mouse events
  svgElem
    .append('rect')
    .attr('class', 'overlay')
    .attr('width', width)
    .attr('height', height)
    .attr('fill', 'none')
    .attr('pointer-events', 'all')
    .on('mouseover', () => focus.style('display', null))
    .on('mouseout', () => focus.style('display', 'none'))
    .on('mousemove', mousemove)
}

// Initial render and watch for data changes
onMounted(() => {
  if (!plot.value) return
  const observer = new ResizeObserver(() => render())
  observer.observe(plot.value)
  onUnmounted(() => observer.disconnect())
})
watch(() => props.curves, render)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "plot",
    ref: plot,
    class: "plot"
  }, null, 512))
}
}

})